import { FC, useEffect, useRef } from 'react'
import { Typography } from '@mui/material'

/**
 * Desafio anti-bot (Cloudflare Turnstile) do formulário público de candidatura.
 *
 * <p>A chave pública vem de `VITE_CAPTCHA_SITE_KEY`, lida em tempo de build. **Sem
 * chave, o widget não renderiza e nenhum token é produzido** — é o modo de
 * desenvolvimento, e combina com `CAPTCHA_ENABLED=false` no backend. Em produção o
 * backend recusa a candidatura sem token, então esquecer a chave aqui quebra o
 * formulário de forma visível, não silenciosa.
 *
 * <p>O script é injetado sob demanda: carregá-lo em toda visita do portal serviria
 * apenas a uma página pública, e o CSP libera o host do Turnstile só por causa dela.
 */

const SITE_KEY = import.meta.env.VITE_CAPTCHA_SITE_KEY as string | undefined
const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: { sitekey: string; callback: (token: string) => void; 'expired-callback'?: () => void }
  ) => string
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

/** Carrega o script uma vez só, mesmo com várias montagens. */
const carregarScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve()
      return
    }
    const existente = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existente) {
      existente.addEventListener('load', () => resolve())
      existente.addEventListener('error', () => reject(new Error('falha ao carregar captcha')))
      return
    }
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('falha ao carregar captcha'))
    document.head.appendChild(script)
  })

interface Props {
  /** Recebe o token do desafio, ou '' quando ele expira. */
  onToken: (token: string) => void
}

export const CaptchaWidget: FC<Props> = ({ onToken }) => {
  const container = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | null>(null)

  useEffect(() => {
    if (!SITE_KEY || !container.current) return

    let vivo = true
    carregarScript()
      .then(() => {
        if (!vivo || !container.current || !window.turnstile) return
        widgetId.current = window.turnstile.render(container.current, {
          sitekey: SITE_KEY,
          callback: (token) => onToken(token),
          // Token do Turnstile expira; limpar avisa o formulário de que ele
          // precisa de um novo antes de enviar.
          'expired-callback': () => onToken(''),
        })
      })
      .catch(() => {
        // Sem widget não há token, e o backend recusa. Nada a fazer aqui além de
        // não quebrar a página.
      })

    return () => {
      vivo = false
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current)
        widgetId.current = null
      }
    }
  }, [onToken])

  if (!SITE_KEY) {
    return (
      <Typography variant="caption" color="text.secondary">
        Verificação anti-robô desativada neste ambiente.
      </Typography>
    )
  }

  return <div ref={container} data-testid="captcha-widget" />
}

/** `true` quando o ambiente exige o desafio — usado para habilitar o botão de envio. */
export const captchaObrigatorio = (): boolean => !!SITE_KEY
